import ConsumerRoutePath from 'app/consumer-app/ConsumerRoutePath';
import {
  useIdentity,
  useUpdatePushNotification,
} from 'app/core/api/company/api-company';
import {
  useDeleteUser,
  useUpdateUserProfileImg,
  useUserProfile,
} from 'app/core/api/user/api-user';
import Button from 'app/shared/components/button/Button';
import Icon from 'app/shared/components/icon/Icon';
import IconLib from 'app/shared/components/icon/icon-lib';
import { DefaultUserImg } from 'app/shared/components/misc';
import SharedRoutePath from 'app/shared/SharedRoutePath';
import { Switch } from '@material-ui/core';
import ClassName from 'app/styles/ClassName';
import { PropsOf } from 'app/tools/general.types';
import clsx from 'clsx';
import { Merge } from 'type-fest';
import { NotificationUpdateRequest } from 'app/core/api/repairer/api-repairer.types';
import EditableImage from 'app/shared/components/app-image/EditableImage';
import {
  useEffectOnChanges,
  useObservableBinding,
} from 'app/tools/hooks/misc-hooks';
import { useShowInfo } from 'app/shared/context-hooks/use-show-info.hook';
import { useLogout } from 'app/core/api/auth/api-auth';
import LoadingOverlay from 'app/shared/page-parts/LoadingOverlay';

const SectionBox: React.FC<{ title: string }> = ({
  title,
  children,
}) => (
  <>
    <h3
      className={clsx(
        'mt-1 mb-0 font-normal text-primary',
        ClassName.NO_HEIGHT_MARGIN
      )}
    >
      {title}
    </h3>
    <div className="flex flex-col rounded border border-solid border-neutral-200">
      {children}
    </div>
  </>
);

const SectionButton: React.VFC<
  Merge<
    PropsOf<typeof Button>,
    { title: string; subtitle: string; icon: IconLib }
  >
> = ({ title, subtitle, icon, ...buttonProps }) => (
  <Button variant="text" className="flex-grow h-btn" {...buttonProps}>
    <Icon {...{ icon }} />
    <div className="flex-grow ml-1">
      <h4 className="m-0 mb-0.2 text-sm font-semibold text-left line-clamp-1">
        {title}
      </h4>
      <div className="text-xs font-light text-left line-clamp-2">
        {subtitle}
      </div>
    </div>
    <Icon icon={IconLib.CHEVRON_RIGHT} />
  </Button>
);

export default function UserProfile(): JSX.Element {
  const { resource: { user } = {}, request: loadIdentity } =
    useIdentity();
  const {
    resource: profile,
    loading,
    request: refresh,
  } = useUserProfile();
  const { request$: updateUserImg$, sending: sendingImg } =
    useUpdateUserProfileImg();
  const { request$: deleteUser$, sending: deletingUser } =
    useDeleteUser();
  const [imageSet, imageSet$] = useObservableBinding<File | null>(
    null
  );
  useEffectOnChanges(() => {
    updateUserImg$({ profilePhoto: imageSet }).subscribe(() =>
      refresh()
    );
  }, [imageSet, refresh, updateUserImg$]);

  const removeImage = () => {
    updateUserImg$({ profilePhoto: null }).subscribe(() => refresh());
  };
  const { request$: updateNotification } =
    useUpdatePushNotification();
  const SwitchStatus = () => {
    const req: NotificationUpdateRequest = {
      enablePushNotification: !user?.enablePushNotification,
    };
    updateNotification(req).subscribe(() => {
      loadIdentity();
    });
  };
  const { showDialog } = useShowInfo();
  const { request$: logout$ } = useLogout();
  const logout = () => {
    logout$().subscribe();
  };
  const handleConfirmDelete = () => {
    deleteUser$().subscribe(() => {
      // eslint-disable-next-line rxjs/no-nested-subscribe
      logout$().subscribe();
    });
  };
  const deleteAccount = () => {
    showDialog({
      title: 'Confirm delete',
      message: `Are you sure you want to delete your account permanently.`,
      actions: [
        { text: 'Cancel' },
        { text: 'Confirm Delete', callback: handleConfirmDelete },
      ],
    });
  };
  const SwitchButton: React.VFC<
    Merge<
      PropsOf<typeof Button>,
      { title: string; subtitle: string; icon: IconLib }
    >
  > = ({ title, subtitle, icon, ...buttonProps }) => (
    <Button
      variant="text"
      className="flex-grow h-btn"
      {...buttonProps}
    >
      <Icon {...{ icon }} />
      <div className="flex-grow ml-1">
        <h4 className="m-0 mb-0.2 text-sm font-semibold text-left line-clamp-1">
          {title}
        </h4>
        <div className="text-xs font-light text-left line-clamp-2">
          {subtitle}
        </div>
      </div>
      <Switch
        defaultChecked={user?.enablePushNotification}
        onChange={() => SwitchStatus()}
      />
    </Button>
  );
  return (
    <>
      {/* <ProfileDrawer
        isRepairerDrawerOpen={isRepairerDrawerOpen}
        setIsRepairerDrawerOpen={setIsRepairerDrawerOpen}
        repairCase={repairCase}
      />
       <WelcomeDrawer
        isWelcomeDrawerOpen={isWelcomeDrawerOpen}
        setIsWelcomeDrawerOpen={setIsWelcomeDrawerOpen}
      /> */}
      <LoadingOverlay frame="outer" loadingComplete={!deletingUser} />
      <div className="flex">
        <EditableImage
          alt="user profile photo"
          currentSrc={profile?.profilePhoto}
          loadingImg={sendingImg || loading}
          usedCustomImg={!!profile?.profilePhoto}
          fallbackImgNode={<DefaultUserImg size={100} />}
          classes={{ img: 'w-10 h-10 rounded object-cover' }}
          {...{ imageSet$, removeImage }}
        />
        <div className="flex flex-col items-start mt-2 ml-1">
          <div className="mb-0.5 text-xl font-semibold line-clamp-2">
            {/* TODO: check if last name is linked to profile */}
            {user?.firstName} {profile?.lastName || user?.lastName}
          </div>
          <a
            href={`mailto: ${user?.email ?? ''})`}
            className="text-xs"
          >
            {user?.email}
          </a>
        </div>
      </div>

      <SectionBox title="Profile">
        <SectionButton
          title="User Info"
          subtitle="View and edit personal details"
          to={ConsumerRoutePath.USER_INFO}
          icon={IconLib.USER}
        />
        <SectionButton
          title="Address"
          subtitle="View and edit your address"
          to={ConsumerRoutePath.USER_ADDRESS}
          icon={IconLib.HOME}
        />
      </SectionBox>

      <SectionBox title="Payment">
        <SectionButton
          title="Payment Information"
          subtitle="Add or remove payment options"
          to={ConsumerRoutePath.WALLET}
          icon={IconLib.WALLET}
        />
      </SectionBox>

      <SectionBox title="Share">
        <SectionButton
          title="Spread the Word"
          subtitle="Refer a friend"
          to={ConsumerRoutePath.REFER}
          icon={IconLib.SEND}
        />
      </SectionBox>

      <SectionBox title="Settings">
        <SwitchButton
          title="Push Notifications"
          subtitle={user?.enablePushNotification ? 'On' : 'Off'}
          icon={IconLib.BELL}
        />
        <SectionButton
          title="Delete Account"
          subtitle="Permanently delete your account"
          onClick={deleteAccount}
          icon={IconLib.REMOVE_PAGE}
        />
        <SectionButton
          title="Change Login"
          subtitle="Choose a new password"
          to={SharedRoutePath.CHANGE_PASSWORD}
          icon={IconLib.LOCK}
        />
        <SectionButton
          title="Logout"
          subtitle="Logout of this device"
          onClick={logout}
          icon={IconLib.EXIT}
        />
      </SectionBox>
    </>
  );
}
